<template>
  <section class="how-section" aria-labelledby="how-it-works-title">
    <div class="how-inner">
      <h2 id="how-it-works-title" class="how-title u-display">
        {{ $t("home.howItWorks.title") }}
      </h2>
      <!-- role="list" is not redundant here: .how-steps sets list-style: none,
           and WebKit drops the implicit list role when a list has no marker, so
           VoiceOver would announce neither "3 items" nor the step numbers (the
           painted step labels are aria-hidden). -->
      <!-- The connector chevrons between cards are ::before pseudo-elements
           on the cards, not sibling <li>s: an <ol> permits only li/script/
           template children, and a decorative li would distort the item count
           in screen readers that ignore aria-hidden on list items. -->
      <ol class="how-steps" role="list">
        <li v-for="(step, index) in steps" :key="step.titleKey" class="how-step">
            <span class="how-step-head">
              <span :class="['how-step-icon', step.iconClass]" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" focusable="false">
                  <path :d="step.iconPath" />
                </svg>
              </span>
              <!-- The list already conveys "item 1 of 3" to assistive tech, so
                   the painted step label is decorative and would otherwise be
                   read twice. -->
              <span class="how-step-number" aria-hidden="true">
                {{ $t("home.howItWorks.stepLabel", { number: index + 1 }) }}
              </span>
            </span>
            <h3 class="how-step-title">{{ $t(step.titleKey) }}</h3>
            <p class="how-step-body">{{ $t(step.bodyKey) }}</p>
            <!-- One concrete beat of the demo conversation per step. The
                 Spanish demo words stay Spanish in every locale (same rule as
                 PhoneChatMockup); only the framing copy localizes. -->
            <p v-if="step.demo === 'correction'" class="how-step-demo">
              <span class="demo-wrong">{{ $t("home.howItWorks.step2DemoWrong") }}</span>
              <span class="demo-arrow" aria-hidden="true">&#8594;</span>
              <span class="demo-right">{{ $t("home.howItWorks.step2DemoRight") }}</span><span class="demo-note">, {{ $t("home.howItWorks.step2DemoNote") }}</span>
            </p>
            <p v-else class="how-step-demo">"{{ $t(step.demoKey) }}"</p>
        </li>
      </ol>
    </div>
  </section>
</template>

<script>
// Drawn marks rather than typed glyphs: the loaded webfonts carry no mic/bulb
// codepoint and emoji render per-OS. Paths are plain data so the template
// stays a single loop.
const ICON_MIC =
  "M12 14a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v5a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-3.08A7 7 0 0 0 19 11h-2z";
const ICON_CHECK = "M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z";
const ICON_BULB =
  "M12 2a7 7 0 0 0-7 7c0 2.4 1.2 4.5 3 5.7V17a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-2.3c1.8-1.2 3-3.3 3-5.7a7 7 0 0 0-7-7zM9 20h6v1a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1v-1z";

export default {
  name: "HowItWorksSection",
  data() {
    return {
      // Literal keys rather than `step${n}Title` built in the template: a
      // constructed key is invisible to grep and to any i18n key extractor.
      steps: [
        {
          titleKey: "home.howItWorks.step1Title",
          bodyKey: "home.howItWorks.step1Body",
          demoKey: "home.howItWorks.step1Demo",
          demo: "quote",
          iconClass: "how-step-icon-mic",
          iconPath: ICON_MIC,
        },
        {
          titleKey: "home.howItWorks.step2Title",
          bodyKey: "home.howItWorks.step2Body",
          demo: "correction",
          iconClass: "how-step-icon-check",
          iconPath: ICON_CHECK,
        },
        {
          titleKey: "home.howItWorks.step3Title",
          bodyKey: "home.howItWorks.step3Body",
          demoKey: "home.howItWorks.step3Demo",
          demo: "quote",
          iconClass: "how-step-icon-memory",
          iconPath: ICON_BULB,
        },
      ],
    };
  },
};
</script>

<style scoped>
/* Marketing routes are light-mode only (the pre-paint stamp in
   public/index.html gates dark on /dashboard/ and native), so this section has
   no dark-mode branch and stays on the hue tokens the sibling landing sections
   use rather than the role-token layer. */

/* width: 100%, never 100vw: 100vw includes the scrollbar gutter, which is what
   put a horizontal scrollbar on this page in the first place. */
.how-section {
  background: var(--al-bg-light);
  width: 100%;
}

.how-inner {
  max-width: 1120px;
  margin: 0 auto;
  padding: 72px 32px;
}

/* DESIGN.md type scale, H2: 26px / 1.16 / 700. It is not breakpoint-split, so
   there is no mobile size override below. */
.how-title {
  font-size: 26px;
  /* public/index.html loads Fraunces at discrete 600/700/800. */
  font-weight: 700;
  line-height: 1.16;
  color: var(--al-ink-900);
  text-align: center;
  margin: 0 0 40px;
  overflow-wrap: break-word;
}

.how-steps {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  align-items: stretch;
  gap: 40px;
  list-style: none;
  margin: 0;
  padding: 0;
}

/* DESIGN.md's card recipe: white surface, subtle --al-gray-300 hairline, small
   shadow, radius 16px, on the tinted page background. */
.how-step {
  background: var(--al-white);
  border: 1px solid var(--al-gray-300);
  border-radius: var(--al-radius-lg);
  box-shadow: var(--al-shadow-sm);
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  position: relative;
  /* Long unbroken words in a locale must not force the grid track wider. */
  min-width: 0;
  overflow-wrap: break-word;
}

/* Connector chevron centered in the 40px grid gap, drawn from two borders so
   it takes its color from the token (a data-URI arrow could not). */
.how-step + .how-step::before {
  content: "";
  position: absolute;
  left: -26px;
  top: 50%;
  width: 10px;
  height: 10px;
  border-top: 2px solid var(--al-gray-400);
  border-right: 2px solid var(--al-gray-400);
  transform: translateY(-50%) rotate(45deg);
}

.how-step-head {
  display: flex;
  align-items: center;
  gap: 10px;
}

.how-step-icon {
  display: flex;
  width: 44px;
  height: 44px;
  border-radius: var(--al-radius-md);
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* Soft tiles mixed from existing accents: DESIGN.md forbids new hex values and
   no *-soft token exists for success or a darker warm text. The marks are
   decorative (aria-hidden), so contrast is not load-bearing. */
.how-step-icon-mic {
  background: var(--al-primary-soft);
  color: var(--al-primary-hover);
}

.how-step-icon-check {
  background: color-mix(in srgb, var(--al-success) 14%, var(--al-white));
  color: var(--al-success-dark);
}

.how-step-icon-memory {
  background: var(--al-warm-soft);
  color: color-mix(in srgb, var(--al-orange) 55%, var(--al-ink-900));
}

/* Caption-weight kicker; the ordinal itself is conveyed by the <ol>. */
.how-step-number {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: var(--al-gray-400);
}

/* DESIGN.md type scale, H3: 20px / 1.24 / 700. */
.how-step-title {
  font-size: 20px;
  font-weight: 700;
  line-height: 1.24;
  color: var(--al-ink-900);
  margin: 0;
}

/* DESIGN.md type scale, Body: 16px / 1.55 / 400. */
.how-step-body {
  font-size: 16px;
  line-height: 1.55;
  color: var(--al-gray);
  margin: 0;
}

/* The demo line anchors to the card bottom so the three chips align across
   cards whose body copy wraps to different heights. */
.how-step-demo {
  margin: auto 0 0;
  background: var(--al-bg-light);
  border: 1px dashed var(--al-gray-300);
  border-radius: var(--al-radius-sm);
  padding: 9px 12px;
  font-size: 13px;
  line-height: 1.5;
  color: var(--al-gray);
  font-style: italic;
}

.demo-wrong {
  font-style: normal;
  color: var(--al-gray-400);
  text-decoration: line-through;
}

.demo-arrow {
  font-style: normal;
  color: var(--al-gray-400);
  margin: 0 4px;
}

.demo-right {
  font-style: normal;
  color: var(--al-success-dark);
  font-weight: 700;
}

.demo-note {
  font-style: italic;
}

/* Tablet padding matches HeroSection's. The strip stays three-up all the way
   to the 767px stack: a two-up intermediate would strand step 3 alone. */
@media (max-width: 1023px) {
  .how-inner {
    padding: 56px 24px;
  }
}

@media (max-width: 767px) {
  .how-inner {
    padding: 48px 16px;
  }

  .how-title {
    margin-bottom: 28px;
  }

  .how-steps {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  /* Stacked cards read top-to-bottom; a rightward chevron between them would
     point sideways at nothing. */
  .how-step + .how-step::before {
    display: none;
  }
}
</style>
